import { LlmArticleSchema } from 'src/types/llm-article-schema';
import {
  getContentSourceLocale,
  getLocaleInfo,
  LocaleInfoTypes,
} from 'subdomains';
import { debugLog, getContentPath } from '.';
import { processData } from './aem-utils';
import { getAemHeaders } from './aem-utils/headers';
import { getAemRedirectUrl } from './aem-utils/redirect';
import { getAEMBasepath } from './basepaths';
import {
  addGoogleAPIKeyToHotelsMap,
  addHotelsToHotelsCarousel,
  addHotelsToHotelsMap,
  addPropertyToComponent,
  modifyComponentsOfType,
} from './component-utils/utils';
import { getPageHeaderData, PageHeader } from './pageheader';
import {
  AEM_MODES,
  assetPath,
  currentEnvironment,
  ENVIRONMENTS,
  isLocal,
  isPreviewMode,
} from './runtime-config';
import type { SearchPanelConfig } from './runtime-config';
import { Logger } from './services/logging';
import { priceGrabPageContent } from './services/price-grab';

export enum SSR_STATUS_CODES {
  SUCCESS = 'success',
  ERROR = 'error',
  REDIRECT = 'redirect',
}

export {
  AEM_MODES,
  assetPath,
  currentEnvironment,
  ENVIRONMENTS,
  isLocal,
  isPreviewMode,
};
export type { SearchPanelConfig };

export interface PageData {
  status: SSR_STATUS_CODES;
  errorCode?: number;
  errorMessage?: null | string;
  content?: any;
  pageHeader?: PageHeader | null;
  localeInfo: string;
  regenerateTime?: string;
}

class AEMFetchStatusError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AEMFetchStatusError';
  }
}

export default async function ssrHelper(
  route: string,
  locale: string = 'en-GB',
) {
  //! PREVIEW
  // https://test1-aem-author.webdev.vholsinternal.co.uk/content/vhols/gb/en.model.json
  // https://test1-viv.webdev.vholsinternal.co.uk/bin/vaahome/AEMPreviewDataExporter?brandPath=content/vhols/gb/en.model.json
  // https://test1-aem-author.webdev.vholsinternal.co.uk/content/vaa-home/gb/en.model.json
  // http://localhost:3000/preview?brandPath=content/launches/2023/11/08/preview_launch_testpage/content/vhols/global/en.model.json

  //! PUBLISH
  // https://test1-vholshome.webdev.vholsinternal.co.uk/gb/en.model.json
  // https://test1-vaahome.webdev.vholsinternal.co.uk/gb/en.model.json
  if (!isPreviewMode) {
    if (getLocaleInfo(locale, LocaleInfoTypes.URL)) {
      return {
        status: 'success',
        errorCode: 0,
        errorMessage: null,
        content: null,
        pageHeader: null,
      };
    }
  }

  try {
    // Feature 655889: Use translatedFrom source locale when content is translated with TransPerfect.
    locale = getContentSourceLocale(locale);

    const backendUrl = getContentPath(route, locale);
    debugLog(`Fetching data from: ${backendUrl}`);

    console.log(
      'SSR Fetching URL: ================>>>>>>>>>>>>>>>>>',
      backendUrl,
    );

    const res = await fetch(backendUrl, { headers: getAemHeaders() });

    if (res.status === 200) {
      const aemData = await res.json();

      const redirectUrl = getAemRedirectUrl(aemData);
      if (redirectUrl) {
        debugLog('Redirect URL:', redirectUrl);
        return {
          status: SSR_STATUS_CODES.REDIRECT,
          errorCode: 0,
          errorMessage: null,
          content: redirectUrl,
        } as PageData;
      }

      const pageContent = await processData(aemData);

      let pageTitle: string = '',
        description: string = '',
        canonicalUrl: string = '',
        searchPanelConfig: SearchPanelConfig = { hideSearchPanel: false },
        llmArticleSchema: LlmArticleSchema | null = null,
        aemLastModifiedDate: string | undefined =
          aemData['lastModifiedDate'] || '';

      if (aemData.hasOwnProperty('vaaPageMetaData')) {
        pageTitle = aemData.vaaPageMetaData.pageTitle;
        description = aemData.vaaPageMetaData['jcr:description'];
        canonicalUrl = aemData.vaaPageMetaData['cq:canonicalUrl'];
        const articleSchema = aemData.vaaPageMetaData['llmArticleSchema'];
        if (articleSchema && typeof articleSchema === 'object') {
          llmArticleSchema = articleSchema;
        }

        searchPanelConfig = {
          ...searchPanelConfig,
          ...(aemData.vaaPageMetaData.hasOwnProperty('hideSearchPanel') && {
            hideSearchPanel: aemData.vaaPageMetaData.hideSearchPanel,
          }),
          ...(aemData.vaaPageMetaData.hasOwnProperty('bookingType') && {
            bookingType: aemData.vaaPageMetaData.bookingType,
          }),
          ...(aemData.vaaPageMetaData.hasOwnProperty('destination') && {
            destination: aemData.vaaPageMetaData.destination,
          }),
        };
      }

      const pageContentWithPriceGrab = await priceGrabPageContent(pageContent);
      const pageContentWithHotels = await modifyComponentsOfType(
        pageContentWithPriceGrab,
        'vaa-home/components/hotels-carousel',
        addHotelsToHotelsCarousel,
      );
      const pageContentWithHotelsCarouselRenderingMode =
        await modifyComponentsOfType(
          pageContentWithHotels,
          'vaa-home/components/hotels-carousel',
          (component: any) =>
            isPreviewMode
              ? addPropertyToComponent(component, 'clientSide', true)
              : component,
        );
      const pageContentWithGoogleMapKey = await modifyComponentsOfType(
        pageContentWithHotelsCarouselRenderingMode,
        'vaa-home/components/hotels-map',
        addGoogleAPIKeyToHotelsMap,
      );
      const pageContentWithHotelsMapHotels = await modifyComponentsOfType(
        pageContentWithGoogleMapKey,
        'vaa-home/components/hotels-map',
        addHotelsToHotelsMap,
      );

      // successful fetch
      const pageData = {
        status: SSR_STATUS_CODES.SUCCESS,
        errorCode: 0,
        errorMessage: null,
        content: pageContentWithHotelsMapHotels,
        pageHeader: await getPageHeaderData(
          pageTitle,
          description,
          canonicalUrl,
          searchPanelConfig,
          aemLastModifiedDate,
          llmArticleSchema,
        ),
        localeInfo: locale,
      };

      return pageData as PageData;
    }

    if (
      res.status === 404 &&
      !isPreviewMode &&
      currentEnvironment === ENVIRONMENTS.PROD
    ) {
      // check if the 404 endpoint is a homepage url
      if (
        backendUrl ===
        getAEMBasepath().main +
          locale.toLowerCase().split('-')[1] +
          '/en.model.json'
      ) {
        // Log the url and raise an alert
        const errorMessage = `Error while fetching data from: ${backendUrl}. Status: ${res.status}. Error: ${res.statusText}`;
        new Logger().error(errorMessage);
      }
    }

    if (res.status !== 404) {
      const errorMessage = `Error while fetching data from: ${backendUrl}. Status: ${res.status}. Error: ${res.statusText}`;
      throw new AEMFetchStatusError(errorMessage);
    }

    // 404s are valid page misses. Return error props so callers can emit notFound.
    return {
      status: SSR_STATUS_CODES.ERROR,
      errorCode: res.status,
      errorMessage: null,
      content: null,
      pageHeader: null,
      localeInfo: locale,
    };
  } catch (e) {
    if (e instanceof AEMFetchStatusError) {
      new Logger().error(e.message);
      throw e;
    }

    // There's an error in the above try block, throw error
    const backendUrl = getContentPath(route, locale);
    const errorMessage = `Error while fetching data. Error: ${e}`;

    new Logger().error(errorMessage);
    throw new Error(errorMessage + ' URL: ' + backendUrl);
  }
}
